import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { clsx } from "clsx";

import { Pill } from "./Pill";
import type { Interaction } from "../types";

/**
 * Vertical timeline of interactions.
 *
 * - Left rail with dots per entry (inbound hollow, outbound filled, internal yellow).
 * - Date-grouped headers: Today / Yesterday / This week / This month / YYYY-MM.
 * - Inbound rows align left, outbound align right — conversation-log visual.
 * - Clicking a row toggles its expanded body.
 * - Jarvis-authored rows get the `.ai-origin` treatment.
 * - When a new entry arrives via live-update while the user is scrolled past
 *   the top, a "N new — jump to latest" pill is sticky.
 */

const TYPE_ICON: Record<string, string> = {
  email: "📧",
  linkedin: "🔗",
  whatsapp: "💬",
  call: "📞",
  meeting: "🤝",
  note: "📝",
  event: "🎟️",
};

const ALLOWED_TAGS = ["a", "p", "br", "em", "i", "strong", "b", "ul", "ol", "li", "code", "pre"];
const ALLOWED_ATTR = ["href", "target", "rel"];

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

function isJarvisAuthored(i: Interaction): boolean {
  const d = i.data;
  // Accept both "jarvis" (new) and "kira" (legacy rows) as provenance markers.
  if (d.logged_by === "jarvis" || d.logged_by === "kira") return true;
  const author = (d.metadata?.source as Record<string, unknown> | undefined)?.author;
  return author === "jarvis" || author === "kira" || d.metadata?.author === "jarvis" || d.metadata?.author === "kira";
}

function classifyBucket(dateIso?: string): string {
  if (!dateIso) return "Undated";
  const t = new Date(dateIso);
  if (isNaN(t.getTime())) return "Undated";
  const now = new Date();
  const day = 24 * 60 * 60 * 1000;
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(t, now)) return "Today";
  const yesterday = new Date(now.getTime() - day);
  if (same(t, yesterday)) return "Yesterday";
  const diff = now.getTime() - t.getTime();
  if (diff < 7 * day) return "This week";
  if (diff < 30 * day) return "This month";
  return t.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

function formatTime(iso?: string): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (isNaN(t.getTime())) return iso.slice(0, 10);
  return t.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ContactTimeline({
  interactions,
  onLoadMore,
  hasMore,
}: {
  interactions: Interaction[];
  onLoadMore?: () => void;
  hasMore?: boolean;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const prevFirstRef = useRef<string | null>(null);

  useEffect(() => {
    const firstId = interactions[0]?.id ?? null;
    // Detect a new entry landing at the top (live insert).
    if (
      prevFirstRef.current !== null &&
      firstId !== null &&
      firstId !== prevFirstRef.current
    ) {
      if (isScrolled) setNewCount((c) => c + 1);
    }
    prevFirstRef.current = firstId;
  }, [interactions, isScrolled]);

  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setIsScrolled(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const groups = useMemo(() => {
    const out: { label: string; items: Interaction[] }[] = [];
    for (const i of interactions) {
      const bucket = classifyBucket(i.data.occurred_at);
      const last = out[out.length - 1];
      if (last && last.label === bucket) last.items.push(i);
      else out.push({ label: bucket, items: [i] });
    }
    return out;
  }, [interactions]);

  if (interactions.length === 0) {
    return (
      <div className="empty-state">
        <div className="jarvis-presence">
          <span className="dot" />
          <span>Timeline is empty — Jarvis will log here as emails arrive.</span>
        </div>
      </div>
    );
  }

  const jumpToLatest = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNewCount(0);
  };

  return (
    <div className="timeline">
      <div ref={topRef} />
      {newCount > 0 ? (
        <button type="button" className="timeline-new-pill" onClick={jumpToLatest}>
          {newCount} new — jump to latest ↑
        </button>
      ) : null}

      {groups.map((g) => (
        <div key={g.label} className="timeline-group">
          <div className="timeline-group-header">{g.label}</div>
          {g.items.map((i) => {
            const d = i.data;
            const direction = d.direction || "internal";
            const type = d.interaction_type || "note";
            const isOpen = expandedId === i.id;
            const isJarvis = isJarvisAuthored(i);
            const bodyHtml = d.content?.summary
              ? d.content.summary
              : d.content?.body ?? "";
            const truncated = bodyHtml.length > 600;
            const displayBody = isOpen || !truncated ? bodyHtml : bodyHtml.slice(0, 600) + "…";
            return (
              <div
                key={i.id}
                className={clsx(
                  "timeline-row",
                  direction === "inbound" && "is-inbound",
                  direction === "outbound" && "is-outbound",
                  direction === "internal" && "is-internal",
                  isJarvis && "ai-origin",
                )}
                onClick={() => setExpandedId(isOpen ? null : i.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandedId(isOpen ? null : i.id);
                  }
                }}
              >
                <span className="timeline-dot" aria-hidden />
                <span className="timeline-icon" aria-hidden>
                  {TYPE_ICON[type] || "•"}
                </span>
                <div className="timeline-body-wrapper">
                  <div className="timeline-subject">{d.subject || type}</div>
                  <div className="timeline-meta">
                    <span>{type}</span>
                    {d.occurred_at ? <span>· {formatTime(d.occurred_at)}</span> : null}
                    {d.direction ? <Pill value={d.direction} /> : null}
                    {d.analysis?.sentiment ? <Pill value={d.analysis.sentiment} /> : null}
                  </div>
                  {d.metadata?.tags && d.metadata.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {d.metadata.tags.map((t) => (
                        <span key={t} className="pill pill--muted">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {isOpen || (displayBody && !truncated) ? (
                    <div
                      className="timeline-body"
                      dangerouslySetInnerHTML={{ __html: sanitize(displayBody) }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {hasMore && onLoadMore ? (
        <div className="flex justify-center pt-2">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onLoadMore}>
            Load earlier
          </button>
        </div>
      ) : null}
    </div>
  );
}
